import { MRT_RowData } from "material-react-table";
import { DelayedSearch } from "src/utils/useDelayedSearch";
import { CustomMRTFilter } from "../mrt.types";
import { FoodsCheckbox, FoodsRadioList } from "@foodpilot/foods";
import { SearchBar } from "src/components/SearchBar/SearchBar";
import { Box, Stack, useTheme } from "@mui/material";
import { onChangeOneFromOne } from "./onChangeOneFromOne";
import { onChangeOneFromMany } from "./onChangeOneFromMany";
import { useTranslation } from "react-i18next";
import type { ComponentAppearance } from "src/components/appearance";

type DisplayStrategy = "radio" | "checkbox";

export type DisplayApi<SelectData> = {
    kind: DisplayStrategy;
    values: SelectData[];
    getId: (item: SelectData) => string | number;
    getName: (item: SelectData) => string | number;
    getValue?: (item: SelectData) => string | number;
};

export const filterValueOf = <SelectData,>(selectOptions: DisplayApi<SelectData>, item: SelectData) =>
    (selectOptions.getValue ?? selectOptions.getName)(item);

type SelectOneFromFilterProps<T extends MRT_RowData, SelectData> = {
    appearance?: ComponentAppearance;
    mrtProps: CustomMRTFilter<T>;
    searchApi: DelayedSearch;
    selectOptions: DisplayApi<SelectData>;
};
export const SelectOneFromFilter = <DataT extends MRT_RowData, SelectData>(
    props: SelectOneFromFilterProps<DataT, SelectData>,
) => {
    const { appearance = "legacy", searchApi, selectOptions, mrtProps } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    const currentFilters = mrtProps.column.getFilterValue() as string | undefined;
    const selectedItem = selectOptions.values.find((item) => filterValueOf(selectOptions, item) === currentFilters);

    return (
        <Stack gap={theme.spacing(2)}>
            <Stack>
                <SearchBar
                    search={searchApi.search}
                    setSearch={searchApi.setSearch}
                    placeholder={
                        appearance === "product-v2" ?
                            t("component.select.searchplaceholder", "Search options…")
                        :   "Rechercher une marque..."
                    }
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- filter popover opens on user action
                    autoFocus
                />
            </Stack>
            <HorizontalBar />
            <Stack
                paddingLeft={theme.spacing(1)}
                sx={appearance === "product-v2" ? { maxHeight: theme.spacing(37.5), overflowY: "auto" } : undefined}
            >
                {selectOptions.kind === "radio" ?
                    <FoodsRadioList
                        selectedItem={selectedItem}
                        onChange={(newValue) => {
                            onChangeOneFromOne({ mrtProps, newValue, selectedItem, selectOptions });
                        }}
                        values={selectOptions.values}
                        getId={selectOptions.getId}
                        getName={selectOptions.getName}
                        FormControlProps={{
                            sx: {
                                flexDirection: "column",
                            },
                        }}
                    />
                : selectOptions.kind === "checkbox" ?
                    <FoodsCheckbox
                        values={selectOptions.values}
                        getId={selectOptions.getId}
                        getName={selectOptions.getName}
                        selectedItems={selectedItem === undefined ? [] : [selectedItem]}
                        onChange={(newValues) => {
                            onChangeOneFromMany({ mrtProps, newValues, selectedItem, selectOptions });
                        }}
                    />
                :   null}
            </Stack>
        </Stack>
    );
};

const HorizontalBar = () => {
    const theme = useTheme();
    return <Box width={"70%"} alignSelf={"center"} borderBottom={`1px solid ${theme.custom.grey[500]}`} />;
};
